import { Component, computed, DestroyRef, effect, inject, OnDestroy, OnInit, signal, untracked } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { APP_CONFIG, IAppSettings } from '@lb/config/interfaces/iapp-settings.interface';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoaderComponent } from '@lb/components/loader/loader.component';
import { ResourceErrorComponent } from '@lb/components/resource-error/resource-error.component';
import { LessonDetailStateService } from '@lb/components/lesson-detail/lesson-detail-state.service';
import { TimezoneAwareDatePipe } from '@lb/pipes';
import { AnalyticsService } from '@lb/services';
import { ComponentLoaderService } from '@melodic-ui/services';
import { PageHeaderComponent } from '@lb/components/page-header/page-header.component';
import { ENROLLMENT_USER_ID_TOKEN, USER_VIEW_TOKEN } from './enrollment-detail.tokens';
import { EnrollmentDetailStateService } from './enrollment-detail-state.service';
import { EnrollmentTimelineComponent } from '../enrollment-timeline-v2/enrollment-timeline.component';
import { NavigationTabGroupComponent, TabItem } from '../navigation-tab-group/navigation-tab-group.component';
import { showsEnrollmentTimeline, usesLessonDetailsV2 } from '@lb/functions';
import { AccountStateService } from '@lb/state/account-state.service';
import { ProgramType } from '@lb/api/mylb/enums/program-type.enum';

@Component({
	selector: 'lb-enrollment-detail-shell',
	standalone: true,
	imports: [RouterOutlet, EnrollmentTimelineComponent, LoaderComponent, ResourceErrorComponent, NavigationTabGroupComponent],
	templateUrl: './enrollment-detail-shell.component.html',
	styleUrl: './enrollment-detail-shell.component.scss',
	providers: [LessonDetailStateService, EnrollmentDetailStateService, TimezoneAwareDatePipe]
})
export class EnrollmentDetailShellComponent implements OnInit, OnDestroy {
	private readonly route = inject(ActivatedRoute);
	private readonly router = inject(Router);
	private readonly destroyRef = inject(DestroyRef);
	private readonly enrollmentDetailStateService = inject(EnrollmentDetailStateService);
	private readonly componentLoaderService = inject(ComponentLoaderService);
	private readonly analytics = inject(AnalyticsService);
	private readonly appSettings = inject<IAppSettings>(APP_CONFIG);
	private readonly accountStateService = inject(AccountStateService);
	protected readonly userView = inject(USER_VIEW_TOKEN, { optional: true }) ?? 'learner';

	// Must be a field so toObservable() runs inside the injection context
	private readonly enrollmentUserID = inject(ENROLLMENT_USER_ID_TOKEN, { optional: true });

	readonly lessonState = inject(LessonDetailStateService);

	public isLearner = this.userView === 'learner';
	protected readonly enrollmentID = signal<number>(0);

	protected enrollment = this.enrollmentDetailStateService.enrollmentDetail;
	protected loading = this.enrollmentDetailStateService.loading;
	protected loaded = this.enrollmentDetailStateService.loaded;
	// Typed resource-level failure (malformed id / not found / no access), surfaced inline via <lb-resource-error>.
	protected resourceError = this.enrollmentDetailStateService.resourceError;

	// Learners never get the timeline; beyond that the shared gate decides (graded enrollments only,
	// and never chirp rooms).
	protected readonly showTimeline = computed(() => !this.isLearner && showsEnrollmentTimeline(this.enrollment()));

	// The Members tab is only offered where it can show anything: chirp room enrollments (cosmetic
	// ProgramType gate, per the D16 note on the enum), viewed by someone the roster endpoint
	// authorizes. Members/guardians match neither branch, so they never see the tab (OQ15); the
	// server still enforces per enrollment for anyone deep-linking.
	private readonly _mayViewRoster = computed(
		() =>
			this.accountStateService.isAdmin() ||
			this.accountStateService.isInstructor() ||
			this.accountStateService.userEntitlements().includes('PartnerContact:App:Access')
	);

	private readonly _showMembersTab = computed(
		() => this.enrollment()?.programTypeID === ProgramType.ChirpRoom && this._mayViewRoster()
	);

	protected readonly tabList = computed<TabItem[]>(() => {
		const id = this.enrollmentID();
		if (!id) return [];

		// Counts appear once their data is in — until then the plain label renders, so the tabs never
		// flash a wrong number. Lessons ride the shell's related-lessons load; members ride the shared
		// roster in EnrollmentDetailStateService (one fetch, reused by the Members tab body).
		const lessonCount = this.lessonState.relatedLessonsLoaded() ? this.lessonState.relatedLessons().length : null;
		const memberCount = this.enrollmentDetailStateService.membersLoaded() ? this.enrollmentDetailStateService.activeMemberCount() : null;

		const tabs: TabItem[] = [
			{ linkText: 'Overview', routerLink: `/enrollments/${id}`, exactMatch: true },
			{ linkText: lessonCount != null ? `Lessons (${lessonCount})` : 'Lessons', routerLink: `/enrollments/${id}/lessons` }
		];

		if (this._showMembersTab()) {
			tabs.push({
				linkText: memberCount != null ? `Members (${memberCount})` : 'Members',
				routerLink: `/enrollments/${id}/members`
			});
		}

		return tabs;
	});

	constructor() {
		// Tag the enrollment context on the recording once it resolves — captures related IDs
		// (learner/instructor/program) the URL doesn't carry, for filtering sessions in Clarity.
		effect(() => {
			const e = this.enrollment();
			if (!e) return;
			this.analytics.setContext({
				enrollmentId: e.enrollmentID,
				learnerId: e.learnerUserID,
				instructorId: e.instructorUserID,
				programId: e.programID,
				languageId: e.languageID
			});
		});

		effect(() => {
			const e = this.enrollment();
			if (!e) return;
			const title = [e.languageName, e.languageLevels].filter(Boolean).join(' ');
			this.componentLoaderService.getContainer('banner-main-content').component.set(PageHeaderComponent);
			this.componentLoaderService.getContainer('banner-main-content').inputs.set({ pageTitle: title });
		});

		effect(() => {
			if (!this.loaded()) return;
			const e = this.enrollment();
			if (!e) return;
			if (this.lessonState.relatedLessonsLoaded()) return;
			untracked(() => this.lessonState.loadRelatedLessons(e.enrollmentID).subscribe());
		});

		// Roster for the Members tab label + body, loaded once for authorized room viewers.
		effect(() => {
			const e = this.enrollment();
			if (!e || !this._showMembersTab()) return;
			untracked(() => this.enrollmentDetailStateService.loadMembers(e.enrollmentID));
		});

		// The v2 detail experience only applies to eligible enrollments (flag on + grading config
		// stamped). Deep links to ineligible enrollments are sent back to the list they came from.
		effect(() => {
			if (!this.loaded()) return;
			const e = this.enrollment();
			if (!e) return;
			if (!usesLessonDetailsV2(e, this.appSettings)) {
				untracked(() => this.router.navigate(['..'], { relativeTo: this.route, replaceUrl: true }));
			}
		});
	}

	private getEnrollmentIDFromRoute(): number {
		// The shell may be mounted at a static path (e.g. 'lessons') rather than
		// directly at the ':id' segment. Walk up ancestors until we find 'id'.
		let snapshot = this.route.snapshot;
		while (snapshot) {
			const id = Number(snapshot.paramMap.get('id'));
			if (id) return id;
			if (!snapshot.parent) break;
			snapshot = snapshot.parent;
		}
		return 0;
	}

	// True when an ':id' route param is present anywhere up the tree (even if it didn't parse
	// to a positive number) — lets us tell a malformed id apart from a route with no id at all.
	private hasRouteIdSegment(): boolean {
		let snapshot: ActivatedRoute['snapshot'] | null = this.route.snapshot;
		while (snapshot) {
			if (snapshot.paramMap.has('id')) return true;
			snapshot = snapshot.parent;
		}
		return false;
	}

	ngOnInit(): void {
		this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			const enrollmentID = this.getEnrollmentIDFromRoute();
			if (!enrollmentID) {
				// A present-but-unparseable id segment (e.g. /enrollments/abc) is a malformed link;
				// flag it inline rather than spinning the loader forever.
				if (this.hasRouteIdSegment()) {
					this.enrollmentDetailStateService.markInvalid();
				}
				return;
			}

			this.enrollmentID.set(enrollmentID);

			this.lessonState.navigationSource.set({ type: 'enrollment', id: enrollmentID });

			// Seed activeLessonID from query param so both the list and timeline
			// start on the right lesson before any scroll or hover occurs.
			const lessonQueryParam = Number(this.route.snapshot.queryParamMap.get('lesson')) || null;
			if (lessonQueryParam) {
				this.lessonState.activeLessonID.set(lessonQueryParam);
				this.lessonState.timelineActiveLessonID.set(lessonQueryParam);
			}

			// If the enrollment isn't available yet (not loaded, or loaded for a different
			// user and the specific enrollment is missing), trigger a load.
			const needsLoad = !this.enrollmentDetailStateService.loaded() || !this.enrollmentDetailStateService.enrollmentDetail();
			if (needsLoad) {
				// Admin context: no user-scoped list available — fetch the single enrollment
				// directly by ID using the admin summary endpoint.
				this.enrollmentDetailStateService.loadEnrollmentDetail(enrollmentID);
			}
		});
	}

	ngOnDestroy(): void {
		this.componentLoaderService.getContainer('banner-main-content').component.set(null);
	}

	onTimelineLessonSelect(lessonID: number): void {
		if (usesLessonDetailsV2(this.enrollment(), this.appSettings)) {
			this.router.navigate(['/enrollments', this.enrollmentID(), 'lessons', lessonID]);
		} else {
			this.lessonState.activeLessonID.set(lessonID);
			this.lessonState.timelineActiveLessonID.set(lessonID);
			this.lessonState.programmaticScrollRequest.set(lessonID);
		}
	}
}
